export interface ApiErrorDetail {
  readonly field: string
  readonly message: string
  readonly type: string
}

interface ApiErrorEnvelope {
  readonly error: {
    readonly code: string
    readonly message: string
    readonly details?: readonly ApiErrorDetail[]
  }
}

/**
 * Erro da API no envelope canônico do backend:
 *   { error: { code, message (pt-BR), details: [{field, message, type}] } }
 *
 * `code` é o contrato estável (47 códigos canônicos — ver os errors.json de i18n).
 * `message` vem SEMPRE em pt-BR: o backend hardcoda e não lê Accept-Language.
 * Traduza pelo `code` (useApiErrorMessage); a message é fallback.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: readonly ApiErrorDetail[]

  constructor(
    status: number,
    code: string,
    message: string,
    details: readonly ApiErrorDetail[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  get isValidation(): boolean {
    return this.status === 422
  }
  get isAuth(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isConflict(): boolean {
    return this.status === 409
  }

  /**
   * details[] → paths do react-hook-form.
   *
   * O backend tem DUAS formas de `field`:
   *   - Pydantic/FastAPI:  "body.categories.0.amount"  (com prefixo)
   *   - field_validation_error dos services: "document", "order_by" (sem prefixo)
   * Normalizar o prefixo faz o path do RHF sair igual nos dois casos.
   */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {}
    for (const d of this.details) {
      const path = d.field.replace(/^(body|query|path|header)\./, '')
      if (path && !(path in out)) out[path] = d.message
    }
    return out
  }
}

/** Falha de rede/DNS/CORS — o request não chegou a ter resposta. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Falha de rede ao contatar a API.')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

function isEnvelope(v: unknown): v is ApiErrorEnvelope {
  if (typeof v !== 'object' || v === null || !('error' in v)) return false
  // `'error' in v` já estreita v — o cast seria redundante (no-unnecessary-type-assertion).
  const e: unknown = v.error
  return typeof e === 'object' && e !== null && 'code' in e && 'message' in e
}

export async function apiErrorFromResponse(res: Response): Promise<ApiError> {
  let body: unknown
  try {
    body = await res.json()
  } catch {
    body = undefined
  }
  if (isEnvelope(body)) {
    return new ApiError(res.status, body.error.code, body.error.message, body.error.details ?? [])
  }
  // 502/504 de proxy, HTML de erro do gateway: nunca passam pelo envelope canônico.
  return new ApiError(res.status, 'http_error', `HTTP ${String(res.status)}`)
}
